import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminBusinessQueryKey, fetchAdminBusinessJson } from '@/lib/queryClient';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { Dashboard, CalendarMonth, EventNote, Person, MoreHoriz } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const tabs = [
  { labelKey: 'nav.dashboard', href: '/admin/dashboard', icon: Dashboard },
  { labelKey: 'nav.calendar', href: '/admin/calendar', icon: CalendarMonth },
  { labelKey: 'nav.bookings', href: '/admin/bookings', icon: EventNote },
  { labelKey: 'nav.customers', href: '/admin/customers', icon: Person },
  { labelKey: 'nav.more', href: '/admin/settings', icon: MoreHoriz },
] as const;

/**
 * Fixed bottom tab bar on small viewports (matches Booking New One mobile admin pattern).
 */
export default function AdminMobileBottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const { data: business } = useQuery({
    queryKey: adminBusinessQueryKey,
    queryFn: fetchAdminBusinessJson,
  });
  const hasBusiness = business != null;

  const value = useMemo(() => {
    const idx = tabs.findIndex(
      (tab) => location.pathname === tab.href || location.pathname.startsWith(`${tab.href}/`),
    );
    return idx >= 0 ? idx : 0;
  }, [location.pathname]);

  return (
    <Paper
      square
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        display: { xs: 'block', md: 'none' },
        pb: 'env(safe-area-inset-bottom, 0px)',
        borderTop: 1,
        borderColor: 'divider',
      }}
      data-testid="nav-mobile-bottom"
    >
      <BottomNavigation value={value} showLabels sx={{ height: 56 }}>
        {tabs.map((tab, i) => {
          const settingsOnly = !hasBusiness && tab.href !== '/admin/settings';
          return (
            <BottomNavigationAction
              key={tab.href}
              label={t(tab.labelKey)}
              icon={<tab.icon sx={{ fontSize: 22 }} />}
              component={Link}
              to={tab.href}
              disabled={settingsOnly}
              value={i}
              data-testid={`nav-tab-${tab.href.replace(/\//g, '-') || 'home'}`}
            />
          );
        })}
      </BottomNavigation>
    </Paper>
  );
}
